import React, { useEffect, useState } from "react";

import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import { Input } from "../../../../../../../../components/Form";
import AddressInfoForm from "../../../../../../../../components/Generic/AddressComponent/AddressInfoForm";
import { DifferentLocation, Location } from "../../../../../../../../models/location";
import { NotifyParty } from "../../../../../../../../models/notify.party";
import { Status } from "../../../../../../../../util/enums";
import { ModalCustom, ModalSection } from "./styles";

interface Props {
  title?: string;
  notifyPartyToUpdate?: NotifyParty;
  hasError?: boolean;
  isOpened: boolean;
  isLoading: boolean;
  onCloseModal: () => void;
  onSubmit: (notifyParty: NotifyParty) => void;
  isView?: boolean;
}

const RoomModal = ({
  title,
  isOpened,
  notifyPartyToUpdate,
  hasError,
  isLoading,
  onCloseModal,
  onSubmit,
  isView,
}: Props) => {
  const { t: translate } = useTranslation();

  const [name, setName] = useState<string>("");
  const [code, setCode] = useState<string>("");
  const [location, setLocation] = useState<Location>();
  const [status, setStatus] = useState<string>(Status.ACTIVE);

  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const [hasFormBeenSubmitted, setHasFormBeenSubmitted] = useState<boolean>(false);

  const disabled = isView || isLoading;

  const getTitle = () => {
    if (isView) {
      return translate("viewNotifyParty");
    }
    if (notifyPartyToUpdate?.id) {
      return translate("editNotifyParty");
    }

    return translate("createNotifyParty");
  };

  const getErrorByField = (field: string) => errors?.find((error) => error.path === field)?.message;

  const schema = yup.object().shape({
    name: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    code: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
  });

  const clearFields = () => {
    setName("");
    setCode("");
    setLocation(undefined);
    setStatus(Status.ACTIVE);
  };

  const handleValidateSuccess = (data?: NotifyParty): boolean => {
    schema.validateSync(
      {
        name: data?.name,
        code: data?.beCode,
        location: data?.location,
        status: data?.status,
      },
      { abortEarly: false }
    );
    setErrors([]);

    return true;
  };

  const handleValidateError = (error: any): boolean => {
    if (error instanceof yup.ValidationError) setErrors(error?.inner);

    return false;
  };

  const isValid = (data?: NotifyParty) => {
    try {
      return handleValidateSuccess(data);
    } catch (error) {
      return handleValidateError(error);
    }
  };

  const mountData = () => {
    return {
      ...notifyPartyToUpdate,
      name,
      beCode: code,
      location,
      status,
    } as NotifyParty;
  };

  const handleSubmit = () => {
    if (!hasFormBeenSubmitted) setHasFormBeenSubmitted(true);

    const data = mountData();

    if (isValid(data)) {
      onSubmit(data);
    }
  };

  useEffect(() => {
    if (notifyPartyToUpdate) {
      setName(notifyPartyToUpdate.name);
      setCode(notifyPartyToUpdate?.beCode || "");
      setLocation(notifyPartyToUpdate?.location);
      setStatus(notifyPartyToUpdate?.status || "");
    }
  }, [notifyPartyToUpdate]);

  useEffect(() => {
    if (hasError !== undefined && !hasError) {
      clearFields();
      onCloseModal();
    }
  }, [hasError]);

  useEffect(() => {
    if (hasFormBeenSubmitted) {
      isValid(mountData());
    }
  }, [hasFormBeenSubmitted, notifyPartyToUpdate, name, code]);

  return (
    <ModalCustom
      data-cy="manageNotifyPartyodal"
      open={isOpened}
      handleClose={onCloseModal}
      title={title || getTitle()}
      dataCyTitle="manageNotifyPartyModalTitle"
      showActionButtons
      actionButtonProps={{
        onCancel: () => {
          clearFields();
          onCloseModal();
        },
        dataCySave: "submitButton",
        dataCyCancel: "cancelButton",
        textSave: notifyPartyToUpdate ? translate("update") : translate("create"),
        loading: isLoading,
        onSave: disabled ? undefined : handleSubmit,
        cancelDisabled: isLoading,
      }}
      labelStatus={translate("status")}
      showStatusField={true}
      statusFieldProps={{
        disabled,
        label: translate("notifyPartyStatus"),
        value: status,
        dataCy: "selectStatus",
        placeholder: translate("selectAnOption"),
        handleChange: (value) => setStatus(value),
      }}
    >
      <ModalSection>
        <Grid container direction="row" alignItems="flex-start" spacing={2}>
          <Grid item md={6} lg={6}>
            <Input
              required
              disabled={disabled}
              name="name"
              label={translate("name")}
              placeholder={translate("name")}
              error={!!(getErrorByField("name") && getErrorByField("name") !== "")}
              errorMessage={getErrorByField("name")}
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </Grid>
          <Grid item md={6} lg={6}>
            <Input
              required
              disabled={disabled}
              name="code"
              label={translate("beCode")}
              placeholder={translate("code")}
              error={!!(getErrorByField("code") && getErrorByField("code") !== "")}
              errorMessage={getErrorByField("code")}
              value={code}
              onChange={(event) => setCode(event.target.value)}
            />
          </Grid>
          <Grid item md={6} lg={6}>
            <AddressInfoForm
              dataCy={"location"}
              addressesArray={[(location || []) as DifferentLocation]}
              view={disabled}
              showMapIcon={false}
              isExpandedAddress={true}
              condition=""
              update={(value) => setLocation(value[0] as Location)}
              hasToShowOnlyOne={true}
              title={translate("locationName")}
              hasError={!!(getErrorByField("location") && getErrorByField("location") !== "")}
              errorMessage={getErrorByField("location")}
            />
          </Grid>
        </Grid>
      </ModalSection>
    </ModalCustom>
  );
};

export default RoomModal;
